<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import HeaderPanel from '@/components/HeaderPanel.vue'

const route = useRoute()

const selectedFile = ref(null)
const userId = ref(null)
const loading = ref(false)
const error = ref('')
const successVisible = ref(false)
const uploadProgress = ref(0)

const apiBase = typeof window !== 'undefined' ? window.location.origin : 'https://ncht.bz'

onMounted(async () => {
  let rawId = null

  if (typeof window !== 'undefined' && !window.Telegram) {
    await new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://telegram.org/js/telegram-web-app.js'
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => resolve()
      document.head.appendChild(script)
    })
  }

  if (typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp) {
    try {
      window.Telegram.WebApp.ready && window.Telegram.WebApp.ready()
    } catch (e) {
      console.log('[transcriber] WebApp.ready error:', e)
    }

    const initDataUnsafe = window.Telegram.WebApp.initDataUnsafe
    const initData = window.Telegram.WebApp.initData

    if (initDataUnsafe && initDataUnsafe.user && initDataUnsafe.user.id) {
      rawId = initDataUnsafe.user.id
    } else if (initData) {
      try {
        const params = new URLSearchParams(initData)
        const userJson = params.get('user')
        if (userJson) {
          const parsed = JSON.parse(userJson)
          if (parsed && parsed.id) {
            rawId = parsed.id
          }
        }
      } catch (e) {
        console.log('[transcriber] Failed to parse initData user:', e)
      }
    }
  }

  if (!rawId && route.query.user_id) {
    rawId = route.query.user_id
  }

  if (rawId && /^-?\d+$/.test(String(rawId))) {
    userId.value = Number(rawId)
  }
})

const fileName = computed(() => selectedFile.value ? selectedFile.value.name : 'Файл не выбран')
const fileSizeHuman = computed(() => {
  if (!selectedFile.value) return ''
  const size = selectedFile.value.size
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} МБ`
  return `${(size / 1024 / 1024 / 1024).toFixed(2)} ГБ`
})

function onFileChange(e) {
  const files = e.target.files
  if (files && files[0]) {
    selectedFile.value = files[0]
  }
}

function onDrop(e) {
  const files = e.dataTransfer.files
  if (files && files[0]) {
    selectedFile.value = files[0]
  }
}

function onDragOver(e) {
  e.preventDefault()
}

async function submit() {
  error.value = ''

  if (!selectedFile.value) {
    error.value = 'Выберите файл для загрузки'
    return
  }

  // Предварительная проверка свободного места на сервере
  try {
    const size = selectedFile.value.size
    const spaceRes = await fetch(`${apiBase}/api/transcriber/space?size=${encodeURIComponent(size)}`)
    const spaceData = await spaceRes.json().catch(() => ({}))
    if (!spaceRes.ok || !spaceData.success) {
      error.value = spaceData.error || 'Не удалось проверить свободное место на сервере'
      return
    }
    if (!spaceData.can_upload) {
      error.value = 'Файл слишком большой: недостаточно свободного места на сервере'
      return
    }
  } catch (e) {
    error.value = e.message || 'Не удалось проверить свободное место на сервере'
    return
  }

  loading.value = true
  uploadProgress.value = 0
  try {
    const formData = new FormData()
    formData.append('file', selectedFile.value)
    if (userId.value != null && !Number.isNaN(Number(userId.value))) {
      formData.append('user_id', String(userId.value))
    }

    const xhr = new XMLHttpRequest()

    const url = `${apiBase}/api/transcriber/file-upload`

    const promise = new Promise((resolve, reject) => {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          uploadProgress.value = Math.round((event.loaded / event.total) * 100)
        }
      }

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText || '{}')
              if (!data.success) {
                reject(new Error(data.error || `Ошибка загрузки: ${xhr.status}`))
              } else {
                resolve({ job_id: data.job_id })
              }
            } catch (e) {
              reject(e)
            }
          } else {
            reject(new Error(`Ошибка загрузки: ${xhr.status}`))
          }
        }
      }

      xhr.onerror = () => reject(new Error('Ошибка сети при загрузке файла'))

      xhr.open('POST', url, true)
      xhr.send(formData)
    })

    await promise

    successVisible.value = true

    setTimeout(() => {
      try {
        if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.close) {
          window.Telegram.WebApp.close()
        } else {
          window.close()
        }
      } catch (_) {
        window.close()
      }
    }, 2000)
  } catch (e) {
    error.value = e.message || 'Неизвестная ошибка'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <HeaderPanel />
  <div class="upload-container">
    <h1 class="title">Загрузите файл</h1>

    <div
      class="dropzone"
      @drop.prevent="onDrop"
      @dragover="onDragOver"
      @dragenter.prevent
    >
      <p class="dropzone-title">Перетащите файл сюда</p>
      <p class="dropzone-subtitle">или нажмите, чтобы выбрать</p>
      <ButtonVue
        label="Выбрать файл"
        class="choose-btn"
        @click="$refs.fileInput.click()"
      />
      <input
        ref="fileInput"
        type="file"
        class="hidden-input"
        accept="audio/*,video/*"
        @change="onFileChange"
      />
      <p class="file-info">
        {{ fileName }}
        <span v-if="fileSizeHuman">({{ fileSizeHuman }})</span>
      </p>
    </div>

    <div class="actions">
      <ButtonVue
        label="Отправить"
        :disabled="loading || !selectedFile"
        @click="submit"
      />
    </div>

    <div v-if="loading" class="progress-wrapper">
      <div class="progress-bar">
        <div class="progress-inner" :style="{ width: uploadProgress + '%' }"></div>
      </div>
      <span class="progress-text">Загружено {{ uploadProgress }}%</span>
    </div>

    <div v-if="error" class="status status-error">
      {{ error }}
    </div>

    <DialogVue
      v-model:visible="successVisible"
      header="Файл загружен"
      :modal="true"
      :closable="false"
      :draggable="false"
    >
      <p>Файл принят в очередь транскрипции. Окно закроется через несколько секунд.</p>
    </DialogVue>
  </div>
</template>

<style scoped>
.upload-container {
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 16px 32px;
  background: var(--surface-ground, #f9fafb);
}

.title {
  margin-bottom: 24px;
  font-size: 24px;
  font-weight: 600;
  color: var(--text-color, #111827);
}

.dropzone {
  border: 2px dashed var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  margin-bottom: 16px;
  background: var(--surface-card, #ffffff);
}

.dropzone-title {
  font-weight: 500;
  margin-bottom: 4px;
  color: var(--text-color, #111827);
}

.dropzone-subtitle {
  margin-bottom: 12px;
  color: var(--text-color-secondary, #6b7280);
}

.choose-btn {
  margin-bottom: 12px;
}

.hidden-input {
  display: none;
}

.file-info {
  font-size: 14px;
  color: var(--text-color-secondary, #4b5563);
  word-break: break-all;
}

.actions {
  margin-top: 8px;
  margin-bottom: 16px;
}

.progress-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  padding: 8px 0;
}

.progress-bar {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: var(--surface-border, #e5e7eb);
  overflow: hidden;
}

.progress-inner {
  height: 100%;
  width: 0;
  border-radius: 999px;
  background: var(--primary-color, #111827);
  transition: width 0.15s ease-out;
}

.progress-text {
  color: var(--text-color-secondary, #4b5563);
}

.status {
  font-size: 14px;
  padding: 10px 12px;
  border-radius: 8px;
}

.status-error {
  background: var(--red-50, #fef2f2);
  color: var(--red-700, #b91c1c);
}
</style>

